import React, { type FC } from 'react'
import { Box, chipClasses, type SvgIconProps, type SxProps, type Theme } from '@mui/material'
import { Delete, PauseCircleFilled, Replay, Search, ToggleOn } from '@mui/icons-material'

import type { ReadonlyRecord } from '../../../../utils'

import { Translation } from '../../../../domain'

import { TextField } from '../../common'
import { Chip, DownloadIcon } from '..'
import { useTranslator } from '../../Internationalization'
import type { ReadonlyPropsWithChildren } from '../../utils'
import {
    useAreThereSelected,
    useExtendedToolbar,
    useIsSearching,
    useOnDownloadCSV,
    useRequiredSelection,
    useSearch,
    useSearchDispatcher,
    useSearchThreshold,
    useSelected,
} from './State'
import type { ActionName, Line } from './Types'

const wrapperStyle: SxProps<Theme> = { display: 'flex', my: 4 }
const Wrapper: FC<ReadonlyPropsWithChildren<{ justifyContent: 'start' | 'space-between', alignItems?: 'start' | 'center' }>> = ({
    justifyContent,
    alignItems = 'center',
    children,
}) => <Box sx={[wrapperStyle, { justifyContent, alignItems }]}>{children}</Box>

const chipStyle: SxProps<Theme> = { my: 1, mx: 2, '&:first-of-type': { ml: 0 } }

const defaultIcons: ReadonlyRecord<ActionName, FC<SvgIconProps>> = { DELETE: Delete, DISABLE: PauseCircleFilled, ENABLE: ToggleOn, RESTART: Replay }
const defaultTranslations: ReadonlyRecord<ActionName, Translation> = {
    DELETE: Translation.DELETE,
    DISABLE: Translation.DISABLE,
    ENABLE: Translation.ENABLE,
    RESTART: Translation.RESTART,
}

const ToolbarButton = <L extends Line,>({ action }: Readonly<{ action: ActionName }>): ReturnType<FC> => {
    const {
        icons: { [action]: Avatar = defaultIcons[action] },
        labels: { [action]: label = defaultTranslations[action] },
        onSelect,
    } = useRequiredSelection<L>()
    const lines = useSelected<L>()
    const { [action]: onClick } = onSelect(lines)

    return onClick ? <Chip sx={chipStyle} avatar={Avatar} label={label} onClick={() => onClick(lines)} /> : null
}

const searchIconStyle: SxProps<Theme> = { mr: 2 }
const buttonsWrapper: SxProps<Theme> = { [`& .${chipClasses.root}`]: { my: 1, mr: 4, '&:last-of-type': { mr: 0 } } }

const UnselectedToolbar: FC = () => {
    const translator = useTranslator()

    const extendedToolbar = useExtendedToolbar()
    const onDownloadCSV = useOnDownloadCSV()

    const isSearching = useIsSearching()
    const searchText = useSearch()
    const dispatchSearch = useSearchDispatcher()
    const searchThreshold = useSearchThreshold()

    if (!isSearching && !extendedToolbar && !onDownloadCSV) {
        /**
         * If there is nothing to display (no Search & extendedToolBar & DownloadCSV)
         * then render nothing
         * */
        return null
    }

    return (
        <Wrapper justifyContent="space-between" alignItems="center">
            <Box sx={buttonsWrapper}>
                {onDownloadCSV ? <Chip label={Translation.DOWNLOAD_CSV} avatar={DownloadIcon} onClick={onDownloadCSV} /> : null}
                {extendedToolbar}
            </Box>
            {isSearching && (
                <TextField
                    data-testid="table-search-input"
                    placeholder={translator.formatTranslation(Translation.SEARCH_PLACEHOLDER, { count: searchThreshold })}
                    startIcon={<Search sx={searchIconStyle} />}
                    value={searchText}
                    onChange={({ target }): void => dispatchSearch(target.value)}
                />
            )}
        </Wrapper>
    )
}

/**
 * The height of 40 derives from the expected height of buttons inside (32) and their margin (4 * 2)
 */
const toolbarStyle: SxProps<Theme> = { minHeight: 40 }
export const Toolbar = <L extends Line,>(): ReturnType<FC> => {
    const selected = useAreThereSelected<L>()

    return selected ? (
        <Wrapper justifyContent="start">
            <Box sx={toolbarStyle}>
                <ToolbarButton action="ENABLE" />
                <ToolbarButton action="DISABLE" />
                <ToolbarButton action="DELETE" />
                <ToolbarButton action="RESTART" />
            </Box>
        </Wrapper>
    ) : (
        <UnselectedToolbar />
    )
}
